
type SafeStorage = {
	getItem<T>(key: string): T | null | undefined;
	setItem<T>(key: string, value: T): void;
	removeItem(key: string): void;
};

function isAvailableInBrowser(storage: Storage): boolean {
	const TEST_KEY: string = "1E1D23CC-7F07-4020-BB78-622F1AEB602C";
	try {
		// Incognito mode for different browsers is implemented in different ways.
		// Some might make localStorage/sessionStorage entirely unavailable (null/undefined), other
		// make it fully available, and then there is Safari which makes it available
		// with a capacity of 0 bytes, I.e. every call to setItem will fail
		storage.setItem(TEST_KEY, TEST_KEY);
		storage.removeItem(TEST_KEY);
		return true;
	} catch (e) {
		return false;
	}
}

const UNDEFINED_PERSISTED_VALUE = "__UNDEFINED__";
const NULL_PERSISTED_VALUE = "__NULL__";


const storageFactory: (storage: Storage) => SafeStorage =
	(storage: Storage) => isAvailableInBrowser(storage)
		? {
			getItem<T>(key: string) {
				const persistedValue: string | null = storage.getItem(key);
				if (persistedValue === null) {
					return null;
				}

				if (persistedValue === UNDEFINED_PERSISTED_VALUE) {
					return undefined;
				} else if (persistedValue === NULL_PERSISTED_VALUE) {
					return null;
				} else {
					return JSON.parse(persistedValue) as T;
				}
			},
			setItem<T>(key: string, value: T) {
				let persistingValue: string;
				if (value === undefined) {
					persistingValue = UNDEFINED_PERSISTED_VALUE;
				} else if (value === null) {
					persistingValue = NULL_PERSISTED_VALUE;
				} else {
					persistingValue = JSON.stringify(value);
				}

				storage.setItem(key, persistingValue);
			},
			removeItem(key: string) {
				storage.removeItem(key);
			}
		}
		: {
			getItem<T>(_key: string): T | null | undefined { return null; },
			setItem<T>(_key: string, _value: T): void { /* NOOP */ },
			removeItem(_key: string): void { /* NOOP */ }
		};

export const safeLocalStorage: SafeStorage = storageFactory(localStorage);
export const safeSessionStorage: SafeStorage = storageFactory(sessionStorage);